import { generatePresignedUrls } from "../actions/files/generatePresignedUrls";

export interface UploadProgress {
  (progress: number): void;
}

export interface UploadFileParams {
  files: File[];
  setProgressCallback: UploadProgress;
}

export interface UploadedFileInfo {
  key: string;
  filename: string;
  url: string;
}

/** Max concurrent S3 PUT uploads to avoid browser connection saturation. */
const UPLOAD_CONCURRENCY = 5;

/**
 * Run an async mapper over items with a concurrency limit.
 * At most `concurrency` promises are in-flight at any time.
 */
async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const idx = nextIndex++;
      results[idx] = await fn(items[idx]);
    }
  }

  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    () => worker()
  );
  await Promise.all(workers);
  return results;
}

/**
 * Standardized file upload helper that handles presigned URL generation and file uploads
 * @param files - Array of files to upload
 * @param setProgressCallback - Callback to update upload progress (0-100)
 * @returns Array of uploaded file info with S3 keys
 */
export const uploadFilesToS3 = async (
  files: File[],
  setProgressCallback: UploadProgress
): Promise<UploadedFileInfo[]> => {
  if (files.length === 0) {
    throw new Error("No files provided for upload");
  }

  // Step 1: Get presigned URLs for all files
  const presignedUrls = await generatePresignedUrls(
    files.map((file) => ({
      filename: file.name,
      contentType: file.type,
      size: file.size,
    }))
  );
  
  // Step 2: Upload files with concurrency limit to avoid browser connection saturation
  let completedCount = 0;
  const progressIncrement = 100 / files.length;

  await mapWithConcurrency(presignedUrls, UPLOAD_CONCURRENCY, async (urlObj) => {
    const file = files.find(f => f.name === urlObj.filename);
    if (!file) {
      throw new Error(`File not found for key ${urlObj.key}`);
    }

    const response = await fetch(urlObj.url, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Upload failed for file: ${file.name}`);
    }

    completedCount++;
    setProgressCallback(Math.min(completedCount * progressIncrement, 100));
  });

  // Ensure we're at 100% after all uploads complete
  setProgressCallback(100);
  
  return presignedUrls;
};

/**
 * Creates a standardized upload function for use with upload modals
 * @param apiCall - Function that calls the appropriate API with uploaded file keys
 * @param clearFormCallback - Function to clear form state after successful upload
 * @returns Upload function compatible with UploadLoaderModal
 */
export const createUploadHandler = <TApiResponse extends { jobId: string; message?: string }>(
  apiCall: (fileKeys: string[]) => Promise<TApiResponse>,
  clearFormCallback: () => void,
  setJobId: (jobId: string) => void
) => {
  return async (
    files: File[],
    setProgressCallback: UploadProgress
  ): Promise<string> => {
    try {
      // Upload files to S3
      const uploadedFiles = await uploadFilesToS3(files, setProgressCallback);
      
      // Call the API with uploaded file keys
      const apiResponse = await apiCall(uploadedFiles.map(file => file.key));
      
      // Set job ID for tracking
      setJobId(apiResponse.jobId);
      
      // Clear form state
      clearFormCallback();
      
      return apiResponse.message || "Process started successfully.";
    } catch (error) {
      console.error('Upload failed:', error);
      throw error;
    }
  };
}; 